import { de, enUS, fr, Locale } from "date-fns/locale";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";
import { ScrapeLevel } from "@/enum/job.enum";
import { ListSourceType, ListStatus } from "@/enum/list.enum";
import { ParallelAiType } from "@/enum/parallel-ai-type.enum";

import { List } from "../../../schema/list";

// A list is locked to "ongoing" when it contains a Parallel AI monitor source,
// because monitors run continuously.
const hasParallelAiMonitor = (data: List): boolean =>
	data.sourceType === ListSourceType.PARALLEL_AI &&
	!!data.parallelAi?.some((source) => source.type === ParallelAiType.MONITOR);

// Scrape-based source arrays. A populated one means the list has something to process.
const hasScrapeSources = (data: List): boolean =>
	[
		data.csvEnrichments,
		data.liCompanyActivity,
		data.liEvents,
		data.liPeople,
		data.liPosts,
		data.liProfileVisits,
		data.jobs,
		data.maps,
		data.liSn,
		data.fundingMa,
		data.swarms,
		data.parallelAi,
		data.companyEnriches,
		data.trackers,
	].some((source) => Array.isArray(source) && source.length > 0);

// A smart list keeps its criteria on the smart-list relation (re-engagement sources,
// a saved filter, or a semantic-search query) rather than on the scrape-source arrays.
const hasSmartListCriteria = (data: List): boolean =>
	!!data.smartList &&
	((Array.isArray(data.smartList.sources) && data.smartList.sources.length > 0) ||
		!!data.smartList.filter ||
		!!data.smartList.semanticSearchProspects);

/**
 * Whether finishing the wizard should prompt the user to activate the list.
 *
 * Activation is what enqueues the processing job, so a paused list that has something
 * to process should prompt for activation. Smart lists store their criteria on the
 * smart-list relation, not on the scrape-source arrays, so they need their own check —
 * without it the prompt never shows, the list is never activated/processed, and it
 * stays empty even though the preview matched contacts.
 */
export const shouldSuggestListActivation = (data: List | undefined): boolean => {
	if (!data || data.status !== ListStatus.PAUSED) {
		return false;
	}

	if (data.sourceType === ListSourceType.SMART_LIST) {
		return hasSmartListCriteria(data);
	}

	return hasScrapeSources(data);
};

/**
 * The display name of the first added source, following the same source-array order the
 * "Added sources" list renders (see AddedSourceList). The placeholder `apollos` source created
 * at list creation is intentionally excluded — its name is the auto-generated list name, which is
 * exactly what we want to replace. Returns null when the list has no named source yet.
 *
 * Used to pre-fill the list name on the first settings visit (see ListEditSetting), so a freshly
 * created list defaults to a meaningful name instead of "{firstName}'s list".
 */
export const getFirstSourceName = (data: List | undefined): string | null => {
	if (!data) {
		return null;
	}

	const sourceArrays: ReadonlyArray<ReadonlyArray<{ name?: string | null }>> = [
		data.maps ?? [],
		data.jobs ?? [],
		data.csvEnrichments ?? [],
		data.liEvents ?? [],
		data.liCompanyActivity ?? [],
		data.liPosts ?? [],
		data.liProfileVisits ?? [],
		data.liProfile ?? [],
		data.liPeople ?? [],
		data.liSn ?? [],
		data.fundingMa ?? [],
		data.swarms ?? [],
		data.parallelAi ?? [],
		data.companyEnriches ?? [],
		data.smartList?.sources ?? [],
	];

	for (const sources of sourceArrays) {
		for (const source of sources) {
			const name = source?.name?.trim();

			if (name) {
				return name;
			}
		}
	}

	return null;
};

/**
 * The name the settings name field should default to. On the first visit of a freshly created
 * list (name still the auto-generated default, i.e. `nameAutoGenerated`), this is the first added
 * source's name when one exists; otherwise the list's existing (user-chosen) name is kept.
 */
export const resolveInitialListName = (data: List): string => {
	if (data.nameAutoGenerated) {
		return getFirstSourceName(data) ?? data.name;
	}

	return data.name;
};

// Locale mapping for date picker
export const localeMap: Record<string, Locale> = {
	"en-US": enUS,
	"en-GB": enUS,
	"fr-FR": fr,
	"de-DE": de,
	de: de,
	en: enUS,
	fr: fr,
};

export const getLocale = (userLocale: string): Locale => {
	return localeMap[userLocale] || enUS;
};

// Check if list frequency should be blocked
export const shouldBlockListFrequency = (data: List | undefined): boolean => {
	if (!data) {
		return false;
	}

	if (data.sourceType === ListSourceType.LI_EVENTS) {
		const oneEventWithMoreThenNow = data.liEvents?.some(
			(event) => event.xDaysBeforeEvent || event.atEventDate
		);

		if (oneEventWithMoreThenNow) {
			return true;
		}
	}

	if (
		data.sourceType === ListSourceType.CSV_ENRICHMENTS ||
		data.sourceType === ListSourceType.CUSTOM_EVENTS ||
		data.sourceType === ListSourceType.JOB_CHANGE ||
		data.sourceType === ListSourceType.TRACKER
	) {
		return true;
	}

	if (hasParallelAiMonitor(data)) {
		return true;
	}

	return false;
};

// Get info message key for blocked list frequency
export const getBlockListFrequencyInfoKey = (
	data: List | undefined,
	isBlocked: boolean
): string | undefined => {
	if (!data || !isBlocked) {
		return undefined;
	}

	if (
		data.sourceType === ListSourceType.CSV_ENRICHMENTS ||
		data.sourceType === ListSourceType.CUSTOM_EVENTS
	) {
		return "blockListFrequencyEnrichment";
	}

	if (data.sourceType === ListSourceType.JOB_CHANGE) {
		return "blockListFrequencyJobChange";
	}

	if (data.sourceType === ListSourceType.TRACKER) {
		return "blockListFrequencyTracker";
	}

	if (data.sourceType === ListSourceType.LI_EVENTS) {
		return "blockListFrequencyEvent";
	}

	if (hasParallelAiMonitor(data)) {
		return "blockListFrequencyMonitor";
	}

	return undefined;
};

// Get dropdown data IDs for view navigation (titles will be translated in component)
export const getDropdownDataIds = (data: List | undefined): string[] => {
	if (!data) {
		return [];
	}

	if (data.specialType !== null || data.sourceType === ListSourceType.EMPTY) {
		return ["leadList", "copywriting", "settings"];
	}

	// TRACKER lists have signals page
	if (data.sourceType === ListSourceType.TRACKER) {
		return ["leadList", "listSources", "signals", "copywriting", "settings"];
	}

	if (
		data.entityType === CsvEnrichmentType.ORGANIZATION ||
		data.scrapeLevel === ScrapeLevel.GET_ENRICHMENT
	) {
		return [
			"leadList",
			"listSources",
			"findPerson",
			"leadQualification",
			"copywriting",
			"settings",
		];
	}

	return ["leadList", "listSources", "copywriting", "settings"];
};
